import { useEffect } from 'react';
import './CachedImage.css';

interface CachedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
    src: string;
    alt: string;
}

// Кэш для предзагруженных изображений - используем Set для отслеживания загруженных URL
const loadedImages = new Set<string>();

// Функция предзагрузки изображения - загружаем через объект Image для кэширования браузером
const preloadImage = (src: string): Promise<void> => {
    // Если изображение уже загружено, сразу разрешаем промис
    if (loadedImages.has(src)) {
        return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            loadedImages.add(src);
            resolve();
        };
        img.onerror = reject;
        img.src = src;
    });
};

export default function CachedImage({
    src,
    alt,
    className = '',
    loading = 'lazy',
    decoding = 'async',
    ...props
}: CachedImageProps) {
    useEffect(() => {
        if (!src) return;

        // Если изображение уже загружено, пропускаем предзагрузку
        if (loadedImages.has(src)) {
            return;
        }

        // Предзагружаем изображение через объект Image
        // Это заставит браузер кэшировать изображение до того, как оно понадобится
        preloadImage(src).catch(() => {
            // Игнорируем ошибки предзагрузки
        });
    }, [src]);

    // Используем обычный img тег - браузер использует кэш после предзагрузки
    return (
        <img
            src={src}
            alt={alt}
            className={className}
            loading={loading}
            decoding={decoding}
            {...props}
        />
    );
}

